import React, { useMemo } from 'react';
import { get as _get } from 'lodash';
import { ProfileData } from '../../types';
import OceanProfileCard from '../personality/OceanProfileCard';
import Icon from '../common/Icon';

interface PersonalityTabProps {
  profile: ProfileData;
  barsAnimated: boolean;
  onSaveToLibrary: () => void;
}

/** Safely parse recommended_tone which may arrive as a JSON-encoded string or an array */
function parseToneArray(raw: string | string[] | null | undefined): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch {
    // treat as comma-separated
  }
  return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
}

const PersonalityTab: React.FC<PersonalityTabProps> = ({
  profile,
  barsAnimated,
  onSaveToLibrary,
}) => {
  const ocean = _get(profile, 'ocean');

  const isValuePresent = (val: any) => val !== null && val !== undefined && val !== 0 && val !== '0';
  const hasOceanData = ocean && (
    isValuePresent(ocean.openness) ||
    isValuePresent(ocean.conscientiousness) ||
    isValuePresent(ocean.extraversion) ||
    isValuePresent(ocean.agreeableness) ||
    isValuePresent(ocean.neuroticism)
  );

  const oceanTraits = _get(profile, 'oceanTraits') || [];
  const disc = _get(profile, 'disc') || null;
  const discTraits = _get(profile, 'discTraits') || [];
  const communication = _get(profile, 'communication') || {};
  const commDescription = _get(communication, 'description') || '';
  const commKeywords = _get(communication, 'keywords') || [];
  
  const decisionMaking = _get(profile, 'decisionMaking') || {};
  const dmDescription = _get(decisionMaking, 'description') || '';
  //const dmPace = _get(decisionMaking, 'pace') || 'N/A';
  //const dmRisk = _get(decisionMaking, 'risk') || 'N/A';
  const dmKeywords = _get(decisionMaking, 'keywords') || [];
  
  const insights = _get(profile, 'insights') || {};
  const motivators = _get(insights, 'motivators') || [];
  
  const meetings = _get(profile, 'meetings') || {};
  const dos = _get(meetings, 'dos') || [];
  const donts = _get(meetings, 'donts') || [];

  // New section data
  const dealPlanning = _get(profile, 'insightForDealPlanning') || null;
  const sellingInsight = _get(profile, 'insightForSelling') || null;
  const coldCalling = _get(profile, 'whenColdCalling') || null;
  const writingEmail = _get(profile, 'whenWritingEmail') || null;
  const phrasesToUse = _get(profile, 'phrases_to_use') || [];
  const phrasesToAvoid = _get(profile, 'phrases_to_avoid') || [];

  const recommendedTone = useMemo(() => {
    return parseToneArray(_get(profile, 'recommended_tone'));
  }, [profile]);

  // Helper: check if deal planning has content
  const hasDealPlanning = dealPlanning && (
    _get(dealPlanning, 'deal_decision_speed') || _get(dealPlanning, 'deal_risk_tolerance')
  );

  // Helper: check if selling insight has content
  const hasSellingInsight = sellingInsight && (
    _get(sellingInsight, 'selling_secret_to_closing') || _get(sellingInsight, 'selling_will_say_no_directly')
  );

  // Helper: check if cold calling has content
  const hasColdCalling = coldCalling && (
    _get(coldCalling, 'selling_cold_call_pattern_interrupt') ||
    _get(coldCalling, 'selling_cold_call_pace') ||
    _get(coldCalling, 'selling_cold_call_tone') ||
    (_get(coldCalling, 'selling_cold_call_tactics') && (_get(coldCalling, 'selling_cold_call_tactics') as string[]).length > 0) ||
    (_get(coldCalling, 'selling_cold_call_mistakes') && (_get(coldCalling, 'selling_cold_call_mistakes') as string[]).length > 0) ||
    _get(coldCalling, 'selling_cold_call_making_ask') ||
    _get(coldCalling, 'selling_cold_call_subconscious_driver')
  );

  // Helper: check if writing email has content
  const hasWritingEmail = writingEmail && (
    _get(writingEmail, 'selling_email_subject') ||
    _get(writingEmail, 'selling_email_salutation') ||
    _get(writingEmail, 'selling_email_greeting') ||
    _get(writingEmail, 'selling_email_bullet_points') ||
    _get(writingEmail, 'selling_email_closing_line') ||
    _get(writingEmail, 'selling_email_complimentary_close') ||
    _get(writingEmail, 'selling_email_tone_of_words') ||
    _get(writingEmail, 'selling_email_overall_messaging') ||
    _get(writingEmail, 'selling_email_length')
  );

  // Contact Insight Card data
  const contactInsight = _get(profile, 'contactInsightCard') || null;
  const hasContactInsight = contactInsight && (
    _get(contactInsight, 'insight_seniority_level') ||
    _get(contactInsight, 'insight_functional_role') ||
    _get(contactInsight, 'insight_risk_tolerance') ||
    _get(contactInsight, 'insight_buying_behavior') ||
    _get(contactInsight, 'insight_preferred_channel') ||
    _get(contactInsight, 'insight_contact_time')
  );

  const contactInsightFields = [
    { label: 'Seniority Level', key: 'insight_seniority_level' },
    { label: 'Functional Role', key: 'insight_functional_role' },
    { label: 'Risk Tolerance', key: 'insight_risk_tolerance' },
    { label: 'Buying Behavior', key: 'insight_buying_behavior' },
    { label: 'Preferred Channel', key: 'insight_preferred_channel' },
    { label: 'Best Contact Time', key: 'insight_contact_time' },
  ];

  // Actionable Outreach Tips data
  const outreachTips = _get(profile, 'actionableOutreachTips') || null;
  const outreachIcebreakers = useMemo(() => {
    return parseToneArray(_get(outreachTips, 'outreach_icebreakers'));
  }, [outreachTips]);
  const outreachPreferredChannels: string[] = _get(outreachTips, 'outreach_preferred_channels') || [];
  const outreachLikes = useMemo(() => {
    return parseToneArray(_get(outreachTips, 'outreach_likes'));
  }, [outreachTips]);
  const outreachDislikes = useMemo(() => {
    return parseToneArray(_get(outreachTips, 'outreach_dislikes'));
  }, [outreachTips]);
  const outreachCommPrefs: string = _get(outreachTips, 'outreach_communication_preferences') || '';
  const hasOutreachTips = outreachTips && (
    outreachIcebreakers.length > 0 ||
    outreachPreferredChannels.length > 0 ||
    outreachLikes.length > 0 ||
    outreachDislikes.length > 0 ||
    outreachCommPrefs
  );

  // Email fields config for rendering the grid
  const emailFields = [
    { label: 'Subject', key: 'selling_email_subject' },
    { label: 'Salutation', key: 'selling_email_salutation' },
    { label: 'Greeting', key: 'selling_email_greeting' },
    { label: 'Bullet Points', key: 'selling_email_bullet_points' },
    { label: 'Closing Line', key: 'selling_email_closing_line' },
    { label: 'Complimentary Close', key: 'selling_email_complimentary_close' },
    { label: 'Tone of Words', key: 'selling_email_tone_of_words' },
    { label: 'Overall Messaging', key: 'selling_email_overall_messaging' },
    { label: 'Length of Mail', key: 'selling_email_length' },
  ];

  if (!hasOceanData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div style={{ textAlign: 'center', padding: '0px 20px', maxWidth: 480 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
            No Intel Available
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {ocean && oceanTraits.length > 0 && (
          <OceanProfileCard
            ocean={ocean}
            oceanTraits={oceanTraits}
            disc={disc}
            discTraits={discTraits}
            animated={barsAnimated}
            onSaveToLibrary={onSaveToLibrary}
          />
        )}

        <div className="space-y-6">
          {commDescription && (
            <div className="bg-brand-aqua rounded-brand p-8 border border-brand-grey01 dot-pattern relative">
              <h4 className="text-[10px] font-extrabold uppercase tracking-widest mb-4">
                Style & Tone
              </h4>
              <p className="text-2xl font-extrabold leading-tight mb-4">
                "{commDescription}"
              </p>
              {commKeywords && commKeywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {(commKeywords || []).map((k: string, idx: number) => (
                    <span
                      key={idx}
                      className="bg-white/50 px-3 py-1 rounded-pill text-[10px] font-black uppercase tracking-wider"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {dmDescription && (
            <div className="bg-brand-black text-white rounded-brand p-8 shadow-xl">
              <h4 className="text-[10px] font-extrabold uppercase tracking-widest mb-6 text-brand-aqua">
                Decision Intelligence
              </h4>
              <p className="text-sm leading-relaxed opacity-90" style={{ marginBottom: dmKeywords && dmKeywords.length > 0 ? '16px' : '24px' }}>
                {dmDescription}
              </p>
              {dmKeywords && dmKeywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {(dmKeywords as string[]).map((k: string, idx: number) => (
                    <span
                      key={idx}
                      className="bg-white/20 px-3 py-1 rounded-pill text-[10px] font-black uppercase tracking-wider text-white"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              )}
              {/*<div className="grid grid-cols-2 gap-4">
                <div className="bg-brand-grey09 p-4 rounded-field border border-brand-grey08">
                  <p className="text-[10px] uppercase font-bold text-brand-pink mb-1">
                    Pace
                  </p>
                  <p className="font-bold text-sm">{dmPace}</p>
                </div>
                <div className="bg-brand-grey09 p-4 rounded-field border border-brand-grey08">
                  <p className="text-[10px] uppercase font-bold text-brand-aqua mb-1">
                    Risk
                  </p>
                  <p className="font-bold text-sm">{dmRisk}</p>
                </div>
              </div>*/}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {motivators && motivators.length > 0 && (
          <div className="bg-white border border-brand-grey01 p-6 rounded-brand shadow-sm">
            <h4 className="text-[10px] font-extrabold uppercase mb-4 text-brand-pink flex items-center gap-1">
              <Icon name="bolt" size="xs" />
              Motivators
            </h4>
            <ul className="text-xs font-bold space-y-3">
              {(motivators || []).map((m: string, idx: number) => (
                <li key={idx} className="flex gap-2">
                  <span>•</span> {m}
                </li>
              ))}
            </ul>
          </div>
        )}

        {dos && dos.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-brand shadow-sm">
            <h4 className="text-[10px] font-extrabold uppercase mb-4 text-emerald-700 flex items-center gap-1">
              <Icon name="check_circle" size="xs" />
              Meeting Do's
            </h4>
            <ul className="text-xs font-bold space-y-3 text-emerald-900">
              {(dos || []).map((d: string, idx: number) => (
                <li key={idx} className="flex gap-2">
                  <span>✓</span> {d}
                </li>
              ))}
            </ul>
          </div>
        )}

        {donts && donts.length > 0 && (
          <div className="bg-rose-50 border border-rose-100 p-6 rounded-brand shadow-sm">
            <h4 className="text-[10px] font-extrabold uppercase mb-4 text-rose-700 flex items-center gap-1">
              <Icon name="cancel" size="xs" />
              Meeting Don'ts
            </h4>
            <ul className="text-xs font-bold space-y-3 text-rose-900">
              {(donts || []).map((d: string, idx: number) => (
                <li key={idx} className="flex gap-2">
                  <span>✕</span> {d}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ── Insights for Deal Planning ──────────────────────────────────────── */}
      {hasDealPlanning && (
        <div className="bg-white border border-brand-grey01 rounded-brand p-8 shadow-sm">
          <h3 className="text-sm font-black uppercase tracking-wider mb-6 flex items-center gap-2">
            <Icon name="trending_up" className="text-brand-pink" size="sm" />
            Insights for Deal Planning
          </h3>
          <div className="space-y-6">
            {_get(dealPlanning, 'deal_decision_speed') && (
              <div>
                <h4 className="text-xs font-extrabold text-brand-black mb-2">
                  How Fast (Or Slow) Will They Move?
                </h4>
                <p className="text-sm text-brand-grey08 leading-relaxed">
                  {_get(dealPlanning, 'deal_decision_speed')}
                </p>
              </div>
            )}
            {_get(dealPlanning, 'deal_risk_tolerance') && (
              <div>
                <h4 className="text-xs font-extrabold text-brand-black mb-2">
                  Can They Take Some Risk Or Not?
                </h4>
                <p className="text-sm text-brand-grey08 leading-relaxed">
                  {_get(dealPlanning, 'deal_risk_tolerance')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Insights for Selling ────────────────────────────────────────────── */}
      {hasSellingInsight && (
        <div className="bg-white border border-brand-grey01 rounded-brand p-8 shadow-sm">
          <h3 className="text-sm font-black uppercase tracking-wider mb-6 flex items-center gap-2">
            <Icon name="sell" className="text-brand-pink" size="sm" />
            Insights For Selling
          </h3>
          <div>
            <h4 className="text-xs font-extrabold text-brand-black mb-4">
              While Negotiating & Closing
            </h4>
            <div className="space-y-5">
              {_get(sellingInsight, 'selling_secret_to_closing') && (
                <div>
                  <h5 className="text-xs font-extrabold text-brand-black mb-1">
                    The secret to closing fast with them is:
                  </h5>
                  <p className="text-sm text-brand-grey08 leading-relaxed">
                    {_get(sellingInsight, 'selling_secret_to_closing')}
                  </p>
                </div>
              )}
              {_get(sellingInsight, 'selling_will_say_no_directly') && (
                <div>
                  <h5 className="text-xs font-extrabold text-brand-black mb-1">
                    Will you ever get a clear answer from them?
                  </h5>
                  <p className="text-sm text-brand-grey08 leading-relaxed">
                    {_get(sellingInsight, 'selling_will_say_no_directly')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── When Cold Calling ───────────────────────────────────────────────── */}
      {hasColdCalling && (
        <div className="bg-white border border-brand-grey01 rounded-brand p-8 shadow-sm">
          <h3 className="text-sm font-black uppercase tracking-wider mb-6 flex items-center gap-2">
            <Icon name="call" className="text-brand-pink" size="sm" />
            When Cold Calling
          </h3>
          <div className="space-y-6">
            {_get(coldCalling, 'selling_cold_call_pattern_interrupt') && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-1">
                  Pattern Interrupt
                </h4>
                <p className="text-sm text-brand-black leading-relaxed">
                  {_get(coldCalling, 'selling_cold_call_pattern_interrupt')}
                </p>
              </div>
            )}
            {_get(coldCalling, 'selling_cold_call_pace') && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-1">
                  Pace
                </h4>
                <p className="text-sm text-brand-black leading-relaxed">
                  {_get(coldCalling, 'selling_cold_call_pace')}
                </p>
              </div>
            )}
            {_get(coldCalling, 'selling_cold_call_tone') && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-1">
                  Tone
                </h4>
                <p className="text-sm text-brand-black leading-relaxed">
                  {_get(coldCalling, 'selling_cold_call_tone')}
                </p>
              </div>
            )}
            {_get(coldCalling, 'selling_cold_call_tactics') && (_get(coldCalling, 'selling_cold_call_tactics') as string[]).length > 0 && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-2">
                  Tactics To Win
                </h4>
                <ul className="text-sm text-brand-black space-y-1">
                  {(_get(coldCalling, 'selling_cold_call_tactics') as string[]).map((t: string, idx: number) => (
                    <li key={idx} className="flex gap-2">
                      <span>•</span> {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {_get(coldCalling, 'selling_cold_call_mistakes') && (_get(coldCalling, 'selling_cold_call_mistakes') as string[]).length > 0 && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-2">
                  Mistakes To Avoid
                </h4>
                <ul className="text-sm text-brand-black space-y-1">
                  {(_get(coldCalling, 'selling_cold_call_mistakes') as string[]).map((m: string, idx: number) => (
                    <li key={idx} className="flex gap-2">
                      <span>•</span> {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {_get(coldCalling, 'selling_cold_call_making_ask') && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-1">
                  Making The Ask
                </h4>
                <p className="text-sm text-brand-black leading-relaxed">
                  {_get(coldCalling, 'selling_cold_call_making_ask')}
                </p>
              </div>
            )}
            {_get(coldCalling, 'selling_cold_call_subconscious_driver') && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-1">
                  Subconscious Driver
                </h4>
                <p className="text-sm text-brand-black leading-relaxed">
                  {_get(coldCalling, 'selling_cold_call_subconscious_driver')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── When Writing An Email ───────────────────────────────────────────── */}
      {hasWritingEmail && writingEmail && (
        <div className="bg-white border border-brand-grey01 rounded-brand p-8 shadow-sm">
          <h3 className="text-sm font-black uppercase tracking-wider mb-6 flex items-center gap-2">
            <Icon name="mail" className="text-brand-pink" size="sm" />
            When Writing An Email
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {emailFields.map((field) => {
              const value = _get(writingEmail, field.key);
              if (!value) return null;
              return (
                <div key={field.key}>
                  <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-1">
                    {field.label}:
                  </h4>
                  <p className="text-sm text-brand-black">
                    {value}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Recommended Tone ───────────────────────────────────────────────── */}
      {recommendedTone.length > 0 && (
        <div className="bg-white border border-brand-grey01 rounded-brand p-8 shadow-sm">
          <h3 className="text-sm font-black uppercase tracking-wider mb-4 flex items-center gap-2">
            <Icon name="record_voice_over" className="text-brand-pink" size="sm" />
            Recommended Tone
          </h3>
          <div className="flex flex-wrap gap-2">
            {recommendedTone.map((tone, idx) => (
              <span
                key={idx}
                className="bg-brand-black text-white px-4 py-1.5 rounded-pill text-[11px] font-black uppercase tracking-wider"
              >
                {tone}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Phrases to Use & Phrases to Avoid ──────────────────────────────── */}
      {(phrasesToUse.length > 0 || phrasesToAvoid.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {phrasesToUse.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-brand shadow-sm">
              <h4 className="text-[10px] font-extrabold uppercase mb-4 text-emerald-700 flex items-center gap-1">
                <Icon name="thumb_up" size="xs" />
                Phrases to Use
              </h4>
              <ul className="text-xs font-bold space-y-3 text-emerald-900">
                {phrasesToUse.map((p: string, idx: number) => (
                  <li key={idx} className="flex gap-2">
                    <span>✓</span> {p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {phrasesToAvoid.length > 0 && (
            <div className="bg-rose-50 border border-rose-100 p-6 rounded-brand shadow-sm">
              <h4 className="text-[10px] font-extrabold uppercase mb-4 text-rose-700 flex items-center gap-1">
                <Icon name="thumb_down" size="xs" />
                Phrases to Avoid
              </h4>
              <ul className="text-xs font-bold space-y-3 text-rose-900">
                {phrasesToAvoid.map((p: string, idx: number) => (
                  <li key={idx} className="flex gap-2">
                    <span>✕</span> {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Contact Insight Card ────────────────────────────────────────────── */}
      {hasContactInsight && contactInsight && (
        <div className="bg-white border border-brand-grey01 rounded-brand p-8 shadow-sm">
          <h3 className="text-sm font-black uppercase tracking-wider mb-6 flex items-center gap-2">
            <Icon name="contact_page" className="text-brand-pink" size="sm" />
            Contact Insight Card
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {contactInsightFields.map((field) => {
              const value = _get(contactInsight, field.key);
              if (!value) return null;
              return (
                <div key={field.key}>
                  <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-1">
                    {field.label}
                  </h4>
                  <p className="text-sm font-bold text-brand-black">
                    {value}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Actionable Outreach Tips ────────────────────────────────────────── */}
      {hasOutreachTips && (
        <div className="bg-white border border-brand-grey01 rounded-brand p-8 shadow-sm">
          <h3 className="text-sm font-black uppercase tracking-wider mb-6 flex items-center gap-2">
            <Icon name="tips_and_updates" className="text-brand-pink" size="sm" />
            Actionable Outreach Tips
          </h3>
          <div className="space-y-6">
            {outreachIcebreakers.length > 0 && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-2">
                  Icebreakers
                </h4>
                <ul className="text-sm text-brand-black space-y-2">
                  {outreachIcebreakers.map((item, idx) => (
                    <li key={idx} className="flex gap-2">
                      <span>•</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {outreachPreferredChannels.length > 0 && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-2">
                  Preferred Channels
                </h4>
                <div className="flex flex-wrap gap-2">
                  {outreachPreferredChannels.map((ch, idx) => (
                    <span
                      key={idx}
                      className="bg-white border border-brand-grey02 px-4 py-1.5 rounded-pill text-[11px] font-bold text-brand-black"
                    >
                      {ch}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(outreachLikes.length > 0 || outreachDislikes.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {outreachLikes.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-2">
                      Likes
                    </h4>
                    <ul className="text-sm text-brand-black space-y-1">
                      {outreachLikes.map((item, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span className="text-emerald-600">•</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {outreachDislikes.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-2">
                      Dislikes
                    </h4>
                    <ul className="text-sm text-brand-black space-y-1">
                      {outreachDislikes.map((item, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span className="text-rose-600">•</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {outreachCommPrefs && (
              <div>
                <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey06 mb-1">
                  Communication Preferences
                </h4>
                <p className="text-sm text-brand-black leading-relaxed">
                  {outreachCommPrefs}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalityTab;
